/**
 * Workspace Scope Provider
 * 
 * Provider component for WorkspaceScopeContext.
 * NO hook exports, NO context creation - just the provider.
 */

import React, { useState, useCallback, ReactNode, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { errorMessage, toError } from '@/lib/errors';
import type { WorkspaceScope } from '@/lib/workspaceScope';
import { WorkspaceScopeContext, type WorkspaceInfo, type WorkspaceMode, type WorkspaceScopeContextValue } from './WorkspaceScope.context';

interface WorkspaceScopeState {
  mode: WorkspaceMode;
  activeWorkspaceId: string | null;
  workspaceIds: string[];
}

const STORAGE_KEY = 'dsos_workspace_scope';

function loadStateFromStorage(): { mode: WorkspaceMode; workspaceId?: string } | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (parsed.mode === 'single' || parsed.mode === 'all') {
      return { mode: parsed.mode, workspaceId: parsed.workspaceId };
    }
  } catch {
    // Invalid storage, ignore
  }
  return null;
}

function saveStateToStorage(mode: WorkspaceMode, workspaceId?: string | null) {
  try {
    const state = { mode, workspaceId: mode === 'single' ? workspaceId : undefined };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    console.warn('[WorkspaceScopeProvider] Failed to save to localStorage:', err);
  }
}

export function WorkspaceScopeProvider({ children }: { children: ReactNode }) {
  const { user, session, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const lastStateRef = useRef<string>('');
  const logChangeRef = useRef(false);
  const [isSwitchingWorkspace, setIsSwitchingWorkspace] = useState(false);
  const previousWorkspaceIdRef = useRef<string | null>(null);

  // CRITICAL: Use session?.user?.id as source of truth (not user?.id)
  // session?.user?.id comes directly from Supabase auth and is always correct
  // Fallback to user?.id for robustness, but prefer session
  const authUserId = session?.user?.id ?? user?.id ?? null;

  // DEV LOGGING: Log auth state
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[WorkspaceScopeProvider] 🔐 Auth state:', {
        authUserId,
        userFromHook: user?.id,
        profileId: user?.id, // Same as authUserId, but logged for clarity
        sessionExists: !!session,
        authLoading,
      });
    }
  }, [authUserId, user?.id, session, authLoading]);

  // CRITICAL: Log workspace initialization (temporary, non-invasive)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && authUserId) {
      console.log('[WorkspaceScopeProvider] WORKSPACE_INIT_OK - Provider mounted, authUserId:', authUserId);
    }
  }, [authUserId]);

  // Fetch workspaces via direct query (robust, no RPC dependency)
  // CRITICAL: Direct query ensures archived_at IS NULL filter is always applied
  // BOOT GATE: Only execute query when authUserId is defined (not null/undefined)
  const { data: workspaces = [], isLoading, error: queryError, refetch } = useQuery({
    queryKey: ['my-workspaces', authUserId],
    queryFn: async (): Promise<WorkspaceInfo[]> => {
      // BOOT GATE: If authUserId is null/undefined, return empty array
      if (!authUserId) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[WorkspaceScopeProvider] ⚠️ Query blocked: authUserId is null/undefined');
        }
        return [];
      }

      try {
        // CRITICAL: Query from workspaces table with join to workspace_members
        // This ensures we can filter archived_at correctly before join
        // Starting from workspaces allows us to filter archived_at directly
        
        // DEV LOGGING: Log query parameters
        if (process.env.NODE_ENV === 'development') {
          console.log('[WorkspaceScopeProvider] 🔍 Starting workspace query:', {
            authUserId, // CRITICAL: Use authUserId (from session?.user?.id)
            userFromHook: user?.id, // For comparison
            queryType: 'workspaces with workspace_members join',
            filters: ['archived_at IS NULL', `workspace_members.user_id = ${authUserId}`],
          });
        }

        // CRITICAL: Use authUserId (session?.user?.id) in query, NOT user?.id
        const { data, error } = await supabase
          .from('workspaces')
          .select(`
            id,
            name,
            created_at,
            archived_at,
            plan,
            workspace_members!inner(
              user_id,
              role
            )
          `)
          .eq('workspace_members.user_id', authUserId) // CRITICAL: Use authUserId, not user.id
          .is('archived_at', null) // CRITICAL: Filter archived workspaces (Quarantine, etc.)
          .order('created_at', { ascending: true });

        // DEV LOGGING: Log raw response
        if (process.env.NODE_ENV === 'development') {
          console.log('[WorkspaceScopeProvider] 📥 Raw query response:', {
            authUserId, // Log authUserId used in query
            error: error ? { message: error.message, code: error.code, details: error.details } : null,
            dataLength: data?.length || 0,
            rawData: data ? data.map((row: any) => ({
              id: row.id,
              name: row.name,
              archived_at: row.archived_at,
              membership: Array.isArray(row.workspace_members) ? row.workspace_members.length : (row.workspace_members ? 1 : 0),
            })) : null,
          });
        }

        if (error) {
          console.error('[WorkspaceScopeProvider] ❌ Query error:', error);
          return [];
        }

        if (!data || !Array.isArray(data)) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[WorkspaceScopeProvider] ⚠️ No data or not array:', { data, isArray: Array.isArray(data) });
          }
          return [];
        }

        // Map to WorkspaceInfo format
        // CRITICAL: Double-check archived_at in JS (defensive filter)
        const mapped: WorkspaceInfo[] = data
          .map((row: any) => {
            // Defensive check: skip if archived_at is not null
            if (!row || row.archived_at !== null) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('[WorkspaceScopeProvider] ⚠️ Skipping archived workspace:', {
                  id: row?.id,
                  name: row?.name,
                  archived_at: row?.archived_at,
                });
              }
              return null;
            }

            // Get role from workspace_members (should be array with one element due to inner join)
            const membership = Array.isArray(row.workspace_members) 
              ? row.workspace_members[0] 
              : row.workspace_members;
            
            if (!membership) {
              console.warn('[WorkspaceScopeProvider] ⚠️ No membership found for workspace:', {
                workspaceId: row.id,
                workspaceName: row.name,
                workspaceMembers: row.workspace_members,
              });
              return null;
            }

            const plan = row.plan != null && row.plan === 'pro' ? 'pro' : 'base';
            return {
              workspace_id: row.id,
              workspace_name: row.name,
              role: membership.role,
              created_at: row.created_at,
              plan: plan as 'base' | 'pro',
            };
          })
          .filter((w): w is WorkspaceInfo => w !== null);

        // DEV LOGGING: Log after mapping
        if (process.env.NODE_ENV === 'development') {
          console.log('[WorkspaceScopeProvider] 📋 After mapping:', {
            mappedCount: mapped.length,
            mapped: mapped.map(w => ({ id: w.workspace_id, name: w.workspace_name, role: w.role })),
          });
        }

        // Deduplicate by workspace_id (safety check)
        const seen = new Set<string>();
        const deduplicated = mapped.filter(w => {
          if (seen.has(w.workspace_id)) {
            console.warn('[WorkspaceScopeProvider] Duplicate workspace filtered:', w.workspace_id, w.workspace_name);
            return false;
          }
          seen.add(w.workspace_id);
          return true;
        });

        // Sort by created_at ascending (oldest first) for consistent ordering
        const sorted = deduplicated.sort((a, b) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

        // Log in dev mode for debugging
        if (process.env.NODE_ENV === 'development') {
          console.log('[WorkspaceScopeProvider] ✅ FINAL RESULT - Fetched workspaces:', {
            authUserId, // Log authUserId used
            count: sorted.length, // CRITICAL: Count workspaces
            workspaces: sorted.map(w => w.workspace_name),
            allWorkspaceIds: sorted.map(w => w.workspace_id),
            fullList: sorted,
          });
          
          // Warn if Quarantine somehow appears (dev-only warning, not a filter)
          const hasQuarantine = sorted.some(w => 
            w.workspace_name?.toLowerCase().includes('quarantine')
          );
          if (hasQuarantine) {
            console.warn('[WorkspaceScopeProvider] ⚠️ WARNING: Quarantine workspace found in results! Check archived_at filter.');
          }
          
          // Warn if no workspaces found
          if (sorted.length === 0) {
            console.warn('[WorkspaceScopeProvider] ⚠️ WARNING: No workspaces found!', {
              authUserId,
              userFromHook: user?.id,
              sessionExists: !!session,
            });
          }
        }

        return sorted;
      } catch (err) {
        console.error('[WorkspaceScopeProvider] Unexpected error:', err);
        return [];
      }
    },
    // BOOT GATE: Only enable query when authUserId is defined (not null/undefined)
    // This prevents query execution with wrong/null user ID
    // CRITICAL: If authUserId is null, query is disabled and will not execute
    enabled: !!authUserId && !authLoading, // Wait for auth to load and ensure authUserId exists
    staleTime: 2 * 60 * 1000, // Reduced staleTime to ensure fresh data on mount
    refetchOnMount: true, // Always refetch on mount to ensure fresh data
  });

  // CRITICAL: Combined loading state
  // Show loading if:
  // 1. Auth is still loading (authLoading)
  // 2. Query is loading AND authUserId exists (waiting for query to complete)
  // AVOID DEADLOCK: If !authLoading and authUserId is null, user is logged out - don't block UI with infinite loading
  const isWorkspaceLoading = authLoading || (isLoading && !!authUserId);

  // Get accessible workspace IDs
  // CRITICAL: This is the source of truth for accessible workspaces
  // It's derived from the workspaces array (which comes from the query)
  // CRITICAL: Must be defined BEFORE any useEffect that uses it to prevent TDZ
  const accessibleWorkspaceIds = useMemo(() => {
    const ids = workspaces.map(w => w.workspace_id);
    
    // DEV LOGGING: Log accessible workspace IDs
    if (process.env.NODE_ENV === 'development') {
      console.log('[WorkspaceScopeProvider] 🔑 Accessible workspace IDs:', {
        count: ids.length,
        ids,
        workspaces: workspaces.map(w => ({ id: w.workspace_id, name: w.workspace_name })),
      });
    }
    
    return ids;
  }, [workspaces]);

  // Initialize state from storage or fallback
  // CRITICAL: Migrate from 'all' mode to 'single' automatically
  const [state, setState] = useState<WorkspaceScopeState>(() => {
    const stored = loadStateFromStorage();
    
    // If we have stored state, use it (will be validated after workspaces load)
    if (stored) {
      if (stored.mode === 'single' && stored.workspaceId) {
        return {
          mode: 'single',
          activeWorkspaceId: stored.workspaceId,
          workspaceIds: [stored.workspaceId],
        };
      } else {
        // CRITICAL: Migrate 'all' mode to 'single' with first workspace
        // This will be finalized after workspaces load, but start with empty to trigger migration
        return {
          mode: 'single',
          activeWorkspaceId: null, // Will be set to first workspace after load
          workspaceIds: [],
        };
      }
    }
    
    // No stored state - default to 'single' with null (will be set to first workspace after load)
    return {
      mode: 'single',
      activeWorkspaceId: null,
      workspaceIds: [],
    };
  });

  // Auto-initialize: if no stored state and workspaces loaded, select first workspace
  useEffect(() => {
    if (!isLoading && workspaces.length > 0 && accessibleWorkspaceIds.length > 0) {
      const stored = loadStateFromStorage();
      
      // If no stored state, select first workspace (workspaces are already sorted by created_at desc)
      if (!stored) {
        const firstWorkspace = workspaces[0];
        if (firstWorkspace) {
          setState({
            mode: 'single',
            activeWorkspaceId: firstWorkspace.workspace_id,
            workspaceIds: [firstWorkspace.workspace_id],
          });
          saveStateToStorage('single', firstWorkspace.workspace_id);
          logChangeRef.current = true;
        }
      } else if (stored.mode === 'single' && stored.workspaceId) {
        // Validate stored workspaceId is still accessible
        const isValid = accessibleWorkspaceIds.includes(stored.workspaceId);
        if (!isValid) {
          // Fallback to first workspace
          const firstWorkspace = workspaces[0];
          if (firstWorkspace) {
            setState({
              mode: 'single',
              activeWorkspaceId: firstWorkspace.workspace_id,
              workspaceIds: [firstWorkspace.workspace_id],
            });
            saveStateToStorage('single', firstWorkspace.workspace_id);
            logChangeRef.current = true;
          }
        } else {
          // Stored workspaceId is valid, ensure state matches
          setState(prev => {
            if (prev.mode === 'single' && prev.activeWorkspaceId === stored.workspaceId) {
              return prev; // Already correct
            }
            return {
              mode: 'single',
              activeWorkspaceId: stored.workspaceId,
              workspaceIds: [stored.workspaceId],
            };
          });
        }
      } else if (stored.mode === 'all' || (!stored.mode && !stored.workspaceId)) {
        // CRITICAL: Migrate from 'all' mode to 'single' with first workspace
        // This ensures 'all' mode is never active in runtime
        const firstWorkspace = workspaces[0];
        if (firstWorkspace) {
          setState({
            mode: 'single',
            activeWorkspaceId: firstWorkspace.workspace_id,
            workspaceIds: [firstWorkspace.workspace_id],
          });
          saveStateToStorage('single', firstWorkspace.workspace_id);
          logChangeRef.current = true;
        }
      }
    }
  }, [isLoading, workspaces, accessibleWorkspaceIds]);

  // CRITICAL: Auto-migrate from 'all' mode to 'single' if somehow still in 'all'
  // This ensures 'all' mode is NEVER active in runtime
  useEffect(() => {
    if (isLoading) return;
    
    // If mode is 'all' or activeWorkspaceId is null, migrate to first workspace
    if (state.mode === 'all' || (!state.activeWorkspaceId && workspaces.length > 0)) {
      const firstWorkspace = workspaces[0];
      if (firstWorkspace) {
        setState({
          mode: 'single',
          activeWorkspaceId: firstWorkspace.workspace_id,
          workspaceIds: [firstWorkspace.workspace_id],
        });
        saveStateToStorage('single', firstWorkspace.workspace_id);
        logChangeRef.current = true;
      }
      return;
    }
    
    // Update workspaceIds when accessible workspaces change (only for 'single' mode)
    if (accessibleWorkspaceIds.length > 0) {
      if (state.mode === 'single' && state.activeWorkspaceId) {
        // Ensure activeWorkspaceId is in workspaceIds
        if (state.workspaceIds.length !== 1 || state.workspaceIds[0] !== state.activeWorkspaceId) {
          setState(prev => ({
            ...prev,
            workspaceIds: [prev.activeWorkspaceId!],
          }));
        }
      }
    } else if (accessibleWorkspaceIds.length === 0 && workspaces.length === 0 && !isLoading) {
      // No workspaces available - this is an error state
      if (process.env.NODE_ENV === 'development') {
        console.error('[WorkspaceScopeProvider] No accessible workspaces found after loading completed');
      }
    }
  }, [isLoading, accessibleWorkspaceIds, state.mode, state.activeWorkspaceId, state.workspaceIds, workspaces.length, queryClient]);

  // Atomic workspace switch - prevents race conditions and ensures consistency
  const switchWorkspaceAtomically = useCallback(async (newWorkspaceId: string) => {
    // Prevent concurrent switches
    if (isSwitchingWorkspace) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[WorkspaceScopeProvider] Switch already in progress, ignoring duplicate request');
      }
      return;
    }

    // Validate workspace is accessible
    if (!isLoading && accessibleWorkspaceIds.length > 0) {
      const isValid = accessibleWorkspaceIds.includes(newWorkspaceId);
      if (!isValid) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[WorkspaceScopeProvider] Invalid workspaceId:', newWorkspaceId, 'Available:', accessibleWorkspaceIds);
        }
        return;
      }
    }

    // Save previous workspace for rollback
    const previousWorkspaceId = state.activeWorkspaceId;
    previousWorkspaceIdRef.current = previousWorkspaceId;

    // Start atomic switch
    setIsSwitchingWorkspace(true);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[WorkspaceScopeProvider] SWITCH_START', {
        from: previousWorkspaceId,
        to: newWorkspaceId,
      });
    }

    try {
      // Step 1: Update state optimistically (UI updates immediately)
      setState({
        mode: 'single',
        activeWorkspaceId: newWorkspaceId,
        workspaceIds: [newWorkspaceId],
      });
      saveStateToStorage('single', newWorkspaceId);

      // Step 2: Update profiles.workspace_id + active_workspace_id in DB (source of truth)
      // CRITICAL: Use authUserId (session?.user?.id ?? user?.id) instead of user?.id
      // Try to update both fields, fallback to workspace_id only if active_workspace_id doesn't exist
      if (authUserId) {
        try {
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ 
              workspace_id: newWorkspaceId,
              active_workspace_id: newWorkspaceId, // For compatibility with legacy code
            })
            .eq('id', authUserId);
          
          if (updateError) {
            // If active_workspace_id column doesn't exist, try with workspace_id only
            if (updateError.message?.includes('active_workspace_id') || updateError.code === '42703') {
              const { error: fallbackError } = await supabase
                .from('profiles')
                .update({ workspace_id: newWorkspaceId })
                .eq('id', authUserId);
              
              if (fallbackError) {
                throw toError(fallbackError, 'Failed to update workspace');
              }
            } else {
              throw toError(updateError, 'Failed to update workspace');
            }
          }
        } catch (err: any) {
          // Re-throw if it's not a column missing error
          if (err?.code !== '42703' && !err?.message?.includes('active_workspace_id')) {
            throw toError(err, 'Failed to switch workspace');
          }
          // Otherwise, try fallback
          const { error: fallbackError } = await supabase
            .from('profiles')
            .update({ workspace_id: newWorkspaceId })
            .eq('id', authUserId);
          
          if (fallbackError) {
            throw toError(fallbackError, 'Failed to update workspace');
          }
        }
      }

      // Step 3: Invalidate and refetch all workspace-dependent queries
      // CRITICAL: Invalidate in order to force refetch with new workspaceId
      // Use exact:false for my-workspaces because queryKey includes authUserId
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['my-workspaces'], exact: false }),
        queryClient.invalidateQueries({ queryKey: ['leads'] }),
        queryClient.invalidateQueries({ queryKey: ['deals'] }),
        queryClient.invalidateQueries({ queryKey: ['calendar-events'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] }),
        queryClient.invalidateQueries({ queryKey: ['revenue-analytics'] }),
        queryClient.invalidateQueries({ queryKey: ['elite-today-plan'] }),
      ]);

      // Step 4: Refetch profile to ensure consistency
      if (authUserId) {
        await queryClient.refetchQueries({ queryKey: ['profile', authUserId] });
      }

      logChangeRef.current = true;

      if (process.env.NODE_ENV === 'development') {
        console.log('[WorkspaceScopeProvider] SWITCH_OK', {
          workspaceId: newWorkspaceId,
        });
      }
    } catch (error: any) {
      // Rollback on error
      if (process.env.NODE_ENV === 'development') {
        console.error('[WorkspaceScopeProvider] SWITCH_FAIL', {
          error: error?.message || error,
          rollingBackTo: previousWorkspaceId,
        });
      }

      // Rollback state
      if (previousWorkspaceId) {
        setState({
          mode: 'single',
          activeWorkspaceId: previousWorkspaceId,
          workspaceIds: [previousWorkspaceId],
        });
        saveStateToStorage('single', previousWorkspaceId);
      } else {
        // If no previous workspace, fallback to first available
        const firstWorkspace = workspaces[0];
        if (firstWorkspace) {
          setState({
            mode: 'single',
            activeWorkspaceId: firstWorkspace.workspace_id,
            workspaceIds: [firstWorkspace.workspace_id],
          });
          saveStateToStorage('single', firstWorkspace.workspace_id);
        }
      }

      // Show error toast
      const { toast } = await import('sonner');
      toast.error('Failed to switch workspace', {
        description: errorMessage(error, 'Please try again'),
      });
    } finally {
      // Always clear switching flag
      setIsSwitchingWorkspace(false);
    }
  }, [isSwitchingWorkspace, isLoading, accessibleWorkspaceIds, state.activeWorkspaceId, workspaces, authUserId, queryClient]);

  // Actions
  // CRITICAL: setMode must be defined BEFORE setActiveWorkspace/setAllMode to avoid TDZ
  const setMode = useCallback(async (newMode: WorkspaceMode, workspaceId?: string | null) => {
    // Case 1: Single workspace mode
    if (newMode === 'single') {
      // Validate workspaceId is provided
      if (!workspaceId) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[WorkspaceScopeProvider] setMode("single") called without workspaceId');
        }
        return;
      }
      
      // Validate workspaceId exists in active workspaces
      const isValidWorkspace = workspaces.some(w => w.workspace_id === workspaceId);
      if (!isValidWorkspace) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[WorkspaceScopeProvider] Invalid workspaceId:', workspaceId, 'Available:', workspaces.map(w => w.workspace_id));
        }
        return;
      }
      
      // Use atomic switch for single workspace mode
      await switchWorkspaceAtomically(workspaceId);
      return;
    }
    
    // Case 2: All workspaces mode
    if (newMode === 'all') {
      // CRITICAL: Use accessibleWorkspaceIds as source of truth (derived from workspaces)
      // Fallback to workspaces.map if accessibleWorkspaceIds is empty (shouldn't happen, but defensive)
      const idsToUse = accessibleWorkspaceIds.length > 0 
        ? accessibleWorkspaceIds 
        : (workspaces.length > 0 ? workspaces.map(w => w.workspace_id) : []);
      
      // If no workspaces available, show error and don't change state
      if (idsToUse.length === 0) {
        if (isLoading) {
          // Still loading - wait for workspaces to load
          if (process.env.NODE_ENV === 'development') {
            console.warn('[WorkspaceScopeProvider] Cannot set all mode: workspaces still loading');
          }
          return;
        }
        
        // Not loading and no workspaces - show error
        if (process.env.NODE_ENV === 'development') {
          console.warn('[WorkspaceScopeProvider] Cannot set all mode: no accessible workspaces. Workspaces count:', workspaces.length);
        }
        const { toast } = await import('sonner');
        toast.error('No workspaces available', {
          description: 'Please contact support if this persists.',
        });
        return;
      }
      
      // Set 'all' mode with all accessible workspace IDs
      // CRITICAL: Do NOT update profiles.workspace_id in 'all' mode (it's a view mode, not a selection)
      setState({
        mode: 'all',
        activeWorkspaceId: null,
        workspaceIds: idsToUse,
      });
      saveStateToStorage('all');
      logChangeRef.current = true;
      
      // Invalidate specific workspace-dependent queries (not all queries)
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['revenue-analytics'] });
      queryClient.invalidateQueries({ queryKey: ['elite-today-plan'] });
      return;
    }
  }, [accessibleWorkspaceIds, workspaces, isLoading, queryClient, switchWorkspaceAtomically]);

  const setActiveWorkspace = useCallback((workspaceId: string) => {
    setMode('single', workspaceId);
  }, [setMode]);

  const setAllMode = useCallback(() => {
    setMode('all');
  }, [setMode]);

  // Compute effective workspace IDs for queries
  // CRITICAL: Never return empty array if user has accessible workspaces
  const effectiveWorkspaceIds = useMemo((): string[] => {
    if (state.mode === 'single' && state.activeWorkspaceId) {
      return [state.activeWorkspaceId];
    }
    
    // For 'all' mode: use state.workspaceIds, but fallback to accessibleWorkspaceIds if empty
    if (state.mode === 'all') {
      if (state.workspaceIds.length > 0) {
        return state.workspaceIds;
      }
      // Fallback: if workspaceIds is empty but accessibleWorkspaceIds has values, use them
      if (accessibleWorkspaceIds.length > 0) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[WorkspaceScopeContext] workspaceIds empty in all mode, using accessibleWorkspaceIds as fallback');
        }
        return accessibleWorkspaceIds;
      }
      // Last resort: if activeWorkspaceId exists, use it (shouldn't happen but prevents empty array)
      if (state.activeWorkspaceId) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[WorkspaceScopeContext] No accessible workspaces, falling back to activeWorkspaceId');
        }
        return [state.activeWorkspaceId];
      }
    }
    
    // Should never reach here, but return empty to prevent crashes
    return [];
  }, [state.mode, state.activeWorkspaceId, state.workspaceIds, accessibleWorkspaceIds]);

  // Compute scope
  // CRITICAL: scope.workspaceIds must never be empty if mode='all' and user has membership
  const scope = useMemo((): WorkspaceScope => {
    if (state.mode === 'single' && state.activeWorkspaceId) {
      return { type: 'single', workspaceId: state.activeWorkspaceId };
    }
    
    // For 'all' mode: ensure workspaceIds is never empty if accessible workspaces exist
    const idsForScope = effectiveWorkspaceIds.length > 0 
      ? effectiveWorkspaceIds 
      : (accessibleWorkspaceIds.length > 0 ? accessibleWorkspaceIds : []);
    
    return { type: 'all', workspaceIds: idsForScope };
  }, [state.mode, state.activeWorkspaceId, effectiveWorkspaceIds, accessibleWorkspaceIds]);

  // Debug logging (only on state change or after fetch)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const stateStr = JSON.stringify({ mode: state.mode, activeWorkspaceId: state.activeWorkspaceId, workspaceIds: state.workspaceIds, effectiveIds: effectiveWorkspaceIds });
      
      if (stateStr !== lastStateRef.current || logChangeRef.current) {
        console.log('[WorkspaceScopeContext] State changed:', {
          mode: state.mode,
          activeWorkspaceId: state.activeWorkspaceId,
          accessibleWorkspaceIds: accessibleWorkspaceIds,
          stateWorkspaceIds: state.workspaceIds,
          effectiveWorkspaceIds: effectiveWorkspaceIds,
          scope: scope.type === 'single' 
            ? { type: 'single', workspaceId: scope.workspaceId } 
            : { type: 'all', workspaceIds: scope.workspaceIds, workspaceIdsLength: scope.workspaceIds.length },
          workspacesCount: workspaces.length,
          workspaceNames: workspaces.map(w => `${w.workspace_name} (${w.workspace_id})`),
        });
        lastStateRef.current = stateStr;
        logChangeRef.current = false;
      }
    }
  }, [state, scope, workspaces.length, effectiveWorkspaceIds, accessibleWorkspaceIds]);

  const error = queryError ? { message: queryError.message } : null;

  const contextValue: WorkspaceScopeContextValue = {
    mode: state.mode,
    activeWorkspaceId: state.activeWorkspaceId,
    workspaceIds: accessibleWorkspaceIds, // All accessible workspaces
    workspaces,
    isSwitchingWorkspace,
    setMode,
    setActiveWorkspace,
    setAllMode,
    scope,
    effectiveWorkspaceIds,
    isLoading: isWorkspaceLoading, // CRITICAL: Use combined loading state (includes authLoading and authUserId check)
    error,
    refetchWorkspaces: () => refetch(),
  };

  return (
    <WorkspaceScopeContext.Provider value={contextValue}>
      {children}
    </WorkspaceScopeContext.Provider>
  );
}

